import { ReactElement, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ButtonBar, PrimaryButton, SecondaryEmailButton } from "./components/Button";
import { useTranslation } from "react-i18next";
import useLayout, { LayoutType } from "./util/useLayout";
import styled from "styled-components";
import { BoycottHeaderProps, EuropeHeaderProps } from "./editor/headerTypes";
import { BackgroundProps } from "./components/BackgroundPicker";
import { GreenHeaderProps } from "./editor/GreenHeaderEditor";
import { useFlyerConfig } from "./util/useFlyerConfig";
import { FlyerType } from "./FlyerTypePicker";
import IconBackground from "./components/IconBackground";
import React from "react";
import { Spacer } from "./editor/EditorHelpers";
import { useIsHardNo } from "./util/useDomain";

export type HeaderProps = BoycottHeaderProps | EuropeHeaderProps | GreenHeaderProps | null;


interface FlyerPreviewProps {
  background: BackgroundProps;
  children: ReactElement | ReactElement[];
  getAnalyticsDetails: () => string;
}

/*
interface AlternativesFlyerPreviewProps {
  headerProps: HeaderProps;
  subheader: string;
  footerProps: FooterProps;
  textColor: string;
  alternatives: AlternativesVP;
}
  */

const blobToData = (blob: Blob): Promise<string> => {
  return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result + '');
      reader.readAsDataURL(blob);
  })
}

/** Loads an external font and returns it as an embeddable font-face with base64 source */
const getFontFaces = async (familyName: string, urls: string[][]) => {
  try {
    const csses: string[] = []
    for (let url of urls) {
      const response = await fetch(url[0]);
      const blob = await response.blob();
      const fontData = await blobToData(blob);
  
      csses.push(`@font-face {
          font-family: "${familyName}";
          font-weight: ${url[1]};
          src: url("${fontData}");
      }`);
    }
    return csses;
  } catch (err) {
      console.error('Error getting font face', err);
      return [];
  }
}

/** Appends a <style> element with provided css to target */
const appendStyles = (csses: string[], target: Element) => {
  const styleEl = document.createElement('style');
  styleEl.className = "temp-style";
  for (let css of csses) {
    styleEl.appendChild(document.createTextNode(css));
  }
  console.log("Append custom font style to element")
  target.appendChild(styleEl);
}

const removeStyles = (target: Element) => {
  for (let styleEl of target.getElementsByClassName("temp-style")) {
    styleEl.remove()
  }
}

export default function FlyerPreview(props: FlyerPreviewProps) {
  const { children, getAnalyticsDetails, background } = props;
  const childArray = React.Children.toArray(children) as ReactElement[];
  const { flyerType, flyerLocale } = useFlyerConfig();
  const flyerRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const setRef = (index: number) => (el: HTMLDivElement | null) => {
    if (el) {
      flyerRefs.current.set(index, el);
    } else {
      flyerRefs.current.delete(index);
    }
  };

  const [fontsReady, setFontsReady] = useState(false);
  const { t } = useTranslation();
  const { isSmallScreen, layout } = useLayout();
  const scale = isSmallScreen ? 8 : 5;
  
  const filename = (flyerType === FlyerType.STAR_STICKER || flyerType === FlyerType.EURO_STICKER)
    ? "sticker"
    : "flyer";
  
  const postDownloadEvent = (downloadType: string) => {

    const formData: Record<string, string> = {
      timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
      downloadType: downloadType,
      flyerType: flyerType,
      flyerLocale: flyerLocale,
      content: getAnalyticsDetails()
    };

    fetch('/api/insert_download.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(formData)
    })
      .then(response => response.json())
      .then(data => {
        if (data.message) {
          console.log("insert_download.php:", data.message);
        } else {
          console.log("insert_download.php error:", data.error);
        }
      })
      .catch(error => console.error('Error:', error));

  }
  
  const waitForFonts = () => {
    return new Promise<void>((resolve) => {
      document.fonts.ready.then(() => {
        setTimeout(() => {
          resolve();
        }, 150);
      });
    });
  };
    
  const renderFlyerCanvas = async (transparentBackground: boolean, index: number) => {
    const fontFaces = await getFontFaces('Montserrat', [
        ["/fonts/Montserrat-Regular.ttf", "400"],
        ["/fonts/Montserrat-SemiBold.ttf", "600"],
        ["/fonts/Montserrat-Bold.ttf", "700"],
      ]);
    const wantsHardno = (flyerType === FlyerType.STAR_STICKER || flyerType === FlyerType.EURO_STICKER);
    if (wantsHardno) {
      const hardnoFontFaces = await getFontFaces('Hardno', [
        ["/fonts/Hardno-Regular.ttf", "400"],
      ]);
      fontFaces.push(...hardnoFontFaces);
    }
      /*
    const svgs = flyerRef.current.getElementsByTagName('svg')
    for (let svg of svgs) {
      appendStyles(fontFaces, svg);
    }
      */
    const svgElements = document.querySelectorAll('.has-custom-font');
    svgElements.forEach(element => {
      appendStyles(fontFaces, element);
    });

    await waitForFonts();

    const flyer = flyerRefs.current.get(index)
    const canvas = await html2canvas(flyer, {
      scale: scale, 
      useCORS: true,
      backgroundColor: transparentBackground ? 'rgba(255, 255, 255, 0.0)' : background.backgroundColor,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight
    });

    /*
    for (let svg of svgs) {
      removeStyles(svg);
    }
      */
    svgElements.forEach(element => {
      removeStyles(element);
    });

    return canvas;    
  }

  useEffect(() => {
    // Fix downloads in Safari
    setTimeout(async () => {
      for (let i = 0; i < childArray.length; i++) {
        // Render canvas for current child index
        const canvas = await renderFlyerCanvas(true, i);
        console.log("prerendered canvas:", canvas)
      }  
    }, 1000);
  }, []);

  const downloadPDF = async () => {
    if (!flyerRefs.current || flyerRefs.current.size === 0) return;
    
    postDownloadEvent("pdf");
    const isLandscape = layout === 'LandscapeFlyer';
    const pdf = new jsPDF({
      orientation: isLandscape ? "landscape" : "portrait",
      unit: "mm",
      format: "a4",
    });
    
    const pageWidth = isLandscape ? 297 : 210;
    
    // Loop through each child in childArray
    for (let i = 0; i < childArray.length; i++) {
      // Render canvas for current child index
      const canvas = await renderFlyerCanvas(true, i);
      const imgData = canvas.toDataURL("image/png");
      
      // Calculate dimensions
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add new page for each child except the first one
      if (i > 0) {
        pdf.addPage();
      }
      
      // Add image to current page
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, undefined, "FAST");
    }
    
    pdf.save(`${filename}.pdf`);
  };
  
  const downloadPNG = async () => {
    if (!flyerRefs.current || flyerRefs.current.size === 0) return;
    
    postDownloadEvent("png")
    
    const canvas = await renderFlyerCanvas(true, 0);
    const imgData = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${filename}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  };

  const downloadJPG = async () => {
    if (!flyerRefs.current || flyerRefs.current.size === 0) return;
    
    postDownloadEvent("jpg")
    
    const canvas = await renderFlyerCanvas(false, 0);
    const imgData = canvas.toDataURL("image/jpeg", 0.6);

    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${filename}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  };

  const { isHardNo } = useIsHardNo();
  const email = isHardNo
    ? "suggestion@hardno.eu"
    : "suggestion@boycott-flyer.eu";

  const subject = `Boycott-flyer suggestion for ${flyerLocale}:${flyerType}`;
  const suggestionMailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}`;

  useEffect(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setFontsReady(true);
      }, 250);
    });
  }, []);

  const flyerContentChildren = childArray.map((child) => !fontsReady
    ? (<div>
      <div style={{ fontFamily: "Montserrat", fontWeight: "400" }}>...</div>
      <div style={{ fontFamily: "Montserrat", fontWeight: "600" }}>...</div>
      <div style={{ fontFamily: "Montserrat", fontWeight: "700" }}>...</div>
    </div>) : child);

  const buttonBar = (<ButtonBar>
    <SecondaryEmailButton href={suggestionMailtoUrl}>{t('make-a-suggestion.button')}</SecondaryEmailButton>
    {(layout === 'PortraitFlyer' || flyerType === FlyerType.BROCHURE) && <PrimaryButton onClick={downloadPDF}>
      {t('download-pdf.button')}
    </PrimaryButton>}
    {layout !== 'PortraitFlyer' && flyerType !== FlyerType.BROCHURE && <PrimaryButton onClick={downloadJPG}>
      {t('download-jpg.button')}
    </PrimaryButton>}
    {flyerType !== FlyerType.BROCHURE && <PrimaryButton onClick={downloadPNG}>
      {t('download-png.button')}
    </PrimaryButton>}
  </ButtonBar>);

  const showButtonBarOnTop = !isSmallScreen && flyerContentChildren.length > 1

  return (
    <FlyerPreviewDiv className="FlyerPreviewDiv" $isSmallScreen={isSmallScreen} $layout={layout}>
      {showButtonBarOnTop ? <>
        <Spacer $height="15px" />
        {buttonBar}
      </> : <Spacer $height="10px" />}
      {flyerContentChildren.map((flyerContentChild, index) => <FlyerContentScrollerForSmallScreenLandscape key={index} $isSmallScreen={isSmallScreen} $layout={layout} className="FlyerContentScrollerForSmallScreenLandscape">
        {<FlyerContentWrapper $isSmallScreen={isSmallScreen} $layout={layout}>
          {<FlyerContent
            id="myFlyer"
            ref={setRef(index)}
            $isSmallScreen={isSmallScreen}
            $layout={layout}
            style={{ 
              ...background,
            }}
          >
            {false && <IconBackground />}
            {true && flyerContentChild}
          </FlyerContent>}
        </FlyerContentWrapper>}
      </FlyerContentScrollerForSmallScreenLandscape>)}
      {!showButtonBarOnTop ? <>
        {buttonBar}
        <Spacer $height="10px" />
      </> : <Spacer $height="10px" />}
      {isSmallScreen && <Spacer $height="120px" />}
    </FlyerPreviewDiv>
  )
}

interface HasLayout {
  $isSmallScreen: boolean;
  $layout: LayoutType;
}

const FlyerPreviewDiv = styled.div<HasLayout>`
  width: ${(props) => props.$isSmallScreen
    ? '100%'
    : (props.$layout === 'LandscapeFlyer' ? 'calc(40px + 702px)' : 'calc(40px + 496px)')};
  min-width: ${(props) => props.$isSmallScreen
    ? '300px'
    : (props.$layout === 'LandscapeFlyer' ? 'calc(40px + 702px)' : 'calc(40px + 496px)')};
  min-height: ${(props) => props.$isSmallScreen ? 'auto' : 'calc(100vh - 55px)'};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: var(--bg-color);
  align-items: center;
  padding: 0px;
  overflow: ${(props) => props.$isSmallScreen ? 'hidden' : 'auto'};
`;

const FlyerContentScrollerForSmallScreenLandscape = styled.div<HasLayout>`
  flex-shrink: 0;
  width: 100%;
  height: ${(props) => props.$isSmallScreen ? 'auto' : 'calc(' + heightFor(props) + ' + 20px)'};
  overflow-x: auto;
  overflow-y: ${(props) => props.$isSmallScreen ? 'visible' : 'hidden'};
`;

const widthFor = (config: HasLayout) => {
  const isSmall = config.$isSmallScreen
  const layout = config.$layout
  if (isSmall) {
    return layout === 'LandscapeFlyer'
      ? 'calc(702px / 1.6)'
      : 'calc(496px / 1.6)' // portrait, circular or other
  }
  return layout === 'LandscapeFlyer'
    ? '702px' 
    : '496px'
}

const heightFor = (config: HasLayout) => {
  const isSmall = config.$isSmallScreen
  const layout = config.$layout
  if (isSmall) {
    return layout === 'PortraitFlyer'
      ? 'calc(702px / 1.6)'
      : 'calc(496px / 1.6)'
  }
  return layout === 'PortraitFlyer'
    ? '702px' 
    : '496px'
}
const borderRadiusFor = (config: HasLayout, isContent = false) => {
  const isSmall = config.$isSmallScreen;
  const layout = config.$layout;
  const factor = isContent ? 5 : 1;
  if (isSmall) {
    return layout === 'Circular'
      ? `calc(496px * ${factor} / 1.6 / 2)`
      : '0px'
  }
  return layout === 'Circular'
    ? `calc(496px * ${factor} / 2)`
    : '0px'

}

const FlyerContentWrapper = styled.div<HasLayout>`
  box-sizing: content-box;
  width: ${(props) => widthFor(props)};
  height: ${(props) => heightFor(props)};
  padding: 0px;
  margin: 10px auto;
  overflow: hidden;
  z-index: 11;
  text-align: center;
  border-radius: ${(props) => borderRadiusFor(props)};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
`;

const FlyerContent = styled.div<HasLayout>`
  width: ${(props) => props.$layout === 'LandscapeFlyer' ? 'calc(5 * 702px)' : 'calc(5 * 496px)'};
  height: ${(props) => props.$layout === 'PortraitFlyer' ? 'calc(5 * 702px)' : 'calc(5 * 496px)'};
  position: relative;
  background: white;
  text-align: center;
  overflow: visible;
  transform: ${(props) => props.$isSmallScreen ? 'scale(calc(0.2 / 1.6))' : 'scale(0.2)'};
  transform-origin: top left;
  border-radius: ${(props) => borderRadiusFor(props, true)};

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  svg .has-custom-font {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

`;
/*-.flyer-content {
-  width: calc(5 * 496px);
-  height: calc(5 * 702px);
-  position: relative;
-  background: white;
-  text-align: center;
-  transform: scale(0.2);
-  transform-origin: top left;
-}
*/

